import React, { useState } from 'react';
import { Mail, Send, CheckCircle, Bell, TrendingUp, Sparkles, Loader2 } from 'lucide-react';

export default function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  React.useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const payload = {
        emailaddress: email,
      };

      await fetch('https://script.google.com/macros/s/AKfycbwACf9Ie9BRN-a922QRd5LSS6Kog1rzswMHXyH8UXbS85viny46wb_poPjo4CgpML37fQ/exec', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      setIsSubmitted(true);
      setIsLoading(false);
      setTimeout(() => {
        setIsSubmitted(false);
        setEmail('');
      }, 3000);
    } catch (err) {
      console.error('Error submitting newsletter:', err);
      setError('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  const benefits = [
    { icon: Bell, text: 'Latest course updates' },
    { icon: TrendingUp, text: 'Industry insights' },
    { icon: Sparkles, text: 'Exclusive offers' },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');

        .nl-section {
          width: 100%;
          background: linear-gradient(135deg, #0b3161 0%, #16365c 100%);
          padding: ${isDesktop ? '60px 0' : '40px 0'};
          font-family: 'DM Sans', sans-serif;
          position: relative;
          overflow: hidden;
        }

        .nl-decor-1 {
          position: absolute;
          width: 300px; height: 300px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(253,214,73,0.1) 0%, transparent 70%);
          top: -100px; right: 10%; pointer-events: none;
        }

        .nl-decor-2 {
          position: absolute;
          width: 200px; height: 200px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(253,214,73,0.08) 0%, transparent 70%);
          bottom: -50px; left: 5%; pointer-events: none;
        }

        .nl-container {
          max-width: 1080px;
          margin: 0 auto;
          padding: 0 40px;
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: ${isDesktop ? '1fr 1fr' : '1fr'};
          gap: ${isDesktop ? '60px' : '40px'};
          align-items: center;
        }

        /* Left Side */
        .nl-content {
          color: #fff;
          text-align: ${isMobile ? 'center' : 'left'};
        }

        .nl-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(253,214,73,0.1);
          border: 1px solid rgba(253,214,73,0.2);
          border-radius: 999px;
          color: #fdd649;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          padding: 6px 14px;
          margin-bottom: 20px;
        }

        .nl-heading {
          font-family: 'Syne', sans-serif;
          font-size: ${isDesktop ? '2.4rem' : '2rem'};
          font-weight: 800;
          line-height: 1.15;
          margin: 0 0 16px 0;
          letter-spacing: -0.5px;
        }

        .nl-desc {
          font-size: 1rem;
          color: rgba(255,255,255,0.85);
          line-height: 1.6;
          margin: 0 0 24px 0;
          max-width: 440px;
        }

        .nl-benefits {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 0px;
        }

        .nl-benefit-item {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 0.95rem;
          justify-content: ${isMobile ? 'center' : 'flex-start'};
        }

        .nl-icon-wrapper {
          width: 32px; height: 32px;
          border-radius: 8px;
          background: rgba(253,214,73,0.15);
          display: flex; align-items: center; justify-content: center;
          color: #fdd649;
        }

        /* Right Side: Form Card */
        .nl-card {
          background: rgba(255,255,255,0.06);
          backdrop-filter: blur(16px);
          border-radius: 20px;
          padding: ${isDesktop ? '32px' : '24px'};
          border: 1px solid rgba(255,255,255,0.1);
          box-shadow: 0 20px 40px rgba(0,0,0,0.25);
        }

        .nl-error {
          padding: 10px 14px;
          margin-bottom: 16px;
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #fca5a5;
          border-radius: 10px;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .nl-input-group {
          position: relative;
          margin-bottom: 16px;
        }

        .nl-input-icon {
          position: absolute;
          left: 16px; top: 50%;
          transform: translateY(-50%);
          color: rgba(255,255,255,0.6);
        }

        .nl-input {
          width: 100%;
          padding: 14px 14px 14px 48px;
          font-size: 0.95rem;
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 12px;
          background: rgba(255,255,255,0.05);
          color: #fff;
          outline: none;
          transition: all 0.3s ease;
          font-family: inherit;
        }
        .nl-input::placeholder { color: rgba(255,255,255,0.5); }
        .nl-input:focus {
          border-color: rgba(253,214,73,0.5);
          box-shadow: 0 0 0 3px rgba(253,214,73,0.15);
        }

        .nl-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 24px;
          background: ${isSubmitted ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#fdd649'};
          color: ${isSubmitted ? '#fff' : '#081426'};
          border: none;
          border-radius: 12px;
          font-size: 0.95rem;
          font-weight: 700;
          cursor: ${isSubmitted || isLoading ? 'default' : 'pointer'};
          transition: all 0.3s ease;
          transform: ${isHovered && !isSubmitted ? 'translateY(-2px)' : 'none'};
          box-shadow: ${isHovered && !isSubmitted ? '0 8px 24px rgba(253,214,73,0.3)' : '0 4px 12px rgba(253,214,73,0.15)'};
        }

        .nl-success {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          margin-top: 16px; padding: 12px;
          background: rgba(16, 185, 129, 0.15);
          border: 1px solid rgba(16, 185, 129, 0.3);
          color: #a7f3d0;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .nl-trust {
          display: flex; align-items: center; justify-content: center; gap: 6px;
          font-size: 0.75rem;
          color: rgba(255,255,255,0.5);
          margin-top: 16px;
        }

        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>

      <section className="nl-section">
        <div className="nl-decor-1" />
        <div className="nl-decor-2" />

        <div className="nl-container">
          {/* Left Content */}
          <div className="nl-content">
            <div className="nl-pill">
              <Mail size={14} /> Newsletter
            </div>

            <h2 className="nl-heading">Join Our Community</h2>

            <p className="nl-desc">
              Subscribe to get exclusive insights, course updates, and industry trends delivered straight to your inbox.
            </p>

            <div className="nl-benefits">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="nl-benefit-item">
                    <div className="nl-icon-wrapper"><Icon size={16} /></div>
                    <span>{benefit.text}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Form Card */}
          <div className="nl-card">
            {error && <div className="nl-error">{error}</div>}

            <div className="nl-input-group">
              <div className="nl-input-icon"><Mail size={18} /></div>
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                onKeyPress={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                className="nl-input"
                disabled={isSubmitted || isLoading}
              />
            </div>

            <button
              onClick={handleSubmit}
              className="nl-btn"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              disabled={isSubmitted || isLoading}
            >
              {isLoading ? (
                <><Loader2 size={18} className="spin" /> Subscribing...</>
              ) : isSubmitted ? (
                <><CheckCircle size={18} /> Subscribed Successfully!</>
              ) : (
                <>Subscribe Now <Send size={16} /></>
              )}
            </button>

            {isSubmitted && (
              <div className="nl-success">
                <CheckCircle size={16} /> Thank you! Check your email.
              </div>
            )}

            <div className="nl-trust">
              <span>🔒</span> We respect your privacy. Unsubscribe anytime.
            </div>
          </div>

        </div>
      </section>
    </>
  );
}